<template>
  <div
    class="doctor-agent-panel"
    :class="{
      'doctor-agent-panel--visible': visible,
      'doctor-agent-panel--float': isFloat,
    }"
    :style="panelStyle"
    ref="panelRef"
  >
    <!-- 嵌入式模式：左边缘拖拽 resize 手柄 -->
    <div
      v-if="!isFloat"
      class="doctor-agent-panel__resize-handle doctor-agent-panel__resize-handle--left"
      @mousedown.stop.prevent="startEmbedResize"
    ></div>

    <!-- 悬浮模式：四边 + 四角 resize 手柄 -->
    <template v-if="isFloat">
      <div class="doctor-agent-panel__resize-handle doctor-agent-panel__resize-handle--n"  @mousedown.stop.prevent="startFloatResize('n', $event)"></div>
      <div class="doctor-agent-panel__resize-handle doctor-agent-panel__resize-handle--s"  @mousedown.stop.prevent="startFloatResize('s', $event)"></div>
      <div class="doctor-agent-panel__resize-handle doctor-agent-panel__resize-handle--e"  @mousedown.stop.prevent="startFloatResize('e', $event)"></div>
      <div class="doctor-agent-panel__resize-handle doctor-agent-panel__resize-handle--w"  @mousedown.stop.prevent="startFloatResize('w', $event)"></div>
      <div class="doctor-agent-panel__resize-handle doctor-agent-panel__resize-handle--ne" @mousedown.stop.prevent="startFloatResize('ne', $event)"></div>
      <div class="doctor-agent-panel__resize-handle doctor-agent-panel__resize-handle--nw" @mousedown.stop.prevent="startFloatResize('nw', $event)"></div>
      <div class="doctor-agent-panel__resize-handle doctor-agent-panel__resize-handle--se" @mousedown.stop.prevent="startFloatResize('se', $event)"></div>
      <div class="doctor-agent-panel__resize-handle doctor-agent-panel__resize-handle--sw" @mousedown.stop.prevent="startFloatResize('sw', $event)"></div>
    </template>

    <!-- 面板头部 -->
    <div
      class="doctor-agent-panel__header"
      :class="{ 'doctor-agent-panel__header--draggable': isFloat }"
      @mousedown="isFloat ? startDrag($event) : null"
    >
      <div class="doctor-agent-panel__header-left">
        <div class="doctor-agent-panel__logo" @click="handleLogoClick">
          <img class="doctor-agent-panel__logo-img" src="@/assets/images/AI-logo.png" alt="AI" />
        </div>
        <div class="doctor-agent-panel__title-group">
          <span class="doctor-agent-panel__title">AI医生助手</span>
          <span class="doctor-agent-panel__status" :class="`doctor-agent-panel__status--${wsStatus}`">
            <span class="doctor-agent-panel__status-dot"></span>
            {{ statusText }}
          </span>
        </div>
      </div>
      <div class="doctor-agent-panel__header-actions" @mousedown.stop>
        <!-- 新建对话 -->
        <button
          class="doctor-agent-panel__action-btn"
          title="新建对话"
          @click="handleNewConversation"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 5v14M5 12h14"/>
          </svg>
        </button>
        <!-- 悬浮/侧边切换 -->
        <button
          class="doctor-agent-panel__action-btn"
          :title="isFloat ? '切换为侧边栏' : '切换为悬浮窗'"
          @click="toggleFloatMode"
        >
          <!-- 侧边模式图标：切换为悬浮 -->
          <svg v-if="!isFloat" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M9 3v18"/>
          </svg>
          <!-- 悬浮模式图标：切换为侧边 -->
          <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M15 3v18"/>
          </svg>
        </button>
        <!-- 关闭 -->
        <button
          class="doctor-agent-panel__action-btn doctor-agent-panel__action-btn--close"
          title="关闭"
          @click="$emit('close')"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <!-- 错误横幅 -->
    <transition name="agent-banner">
      <div v-if="errorBanner" class="doctor-agent-panel__error-banner">
        ⚠️ {{ errorBanner }}
      </div>
    </transition>

    <!-- 连接中占位 -->
    <div v-if="wsStatus === 'connecting'" class="doctor-agent-panel__connecting">
      <span class="doctor-agent-panel__connecting-spinner"></span>
      <span>正在连接 AI 助手...</span>
    </div>

    <!-- 连接失败占位 -->
    <div v-else-if="wsStatus === 'error'" class="doctor-agent-panel__error-state">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="#e34d59" stroke-width="1.5"/>
        <line x1="12" y1="8" x2="12" y2="12" stroke="#e34d59" stroke-width="2" stroke-linecap="round"/>
        <circle cx="12" cy="16" r="1" fill="#e34d59"/>
      </svg>
      <p>连接失败，请检查网络</p>
      <button class="doctor-agent-panel__retry-btn" @click="handleRetryConnect">重新连接</button>
    </div>

    <!-- 聊天区域 -->
    <template v-else>
      <AgentChatWindow
        :messages="messages"
        :is-typing="isTyping"
        :show-welcome="false"
        @retry="handleRetryMessage"
        @quick-send="handleSend"
        @approval-decision="handleApprovalDecision"
        @card-action="handleCardAction"
      />
      <AgentMessageInput
        :disabled="wsStatus !== 'connected'"
        :is-generating="isGenerating || isTyping"
        @send="handleSend"
        @stop="handleStop"
      />
      <!-- 底部免责声明 -->
      <div class="doctor-agent-panel__disclaimer">内容由 AI 生成，仅供参考</div>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from '@vue/composition-api';
import { useAgentChat } from './useAgentChat';
import AgentChatWindow from './AgentChatWindow.vue';
import AgentMessageInput from './AgentMessageInput.vue';

const FLOAT_MODE_KEY = 'doctor-agent-float-mode';
const FLOAT_POS_KEY = 'doctor-agent-float-pos';
const FLOAT_SIZE_KEY = 'doctor-agent-float-size';
const EMBED_WIDTH_KEY = 'doctor-agent-embed-width';

// 嵌入式宽度限制
const EMBED_MIN_WIDTH = 320;
const EMBED_MAX_WIDTH = 700;
const EMBED_DEFAULT_WIDTH = 420;

// 悬浮模式尺寸限制
const FLOAT_MIN_WIDTH = 320;
const FLOAT_MAX_WIDTH = 800;
const FLOAT_MIN_HEIGHT = 400;
const FLOAT_DEFAULT_WIDTH = 400;
const FLOAT_DEFAULT_HEIGHT = 600;

export default {
  name: 'DoctorAgentPanel',
  components: { AgentChatWindow, AgentMessageInput },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** 从 PC 端 doctorInfo 传入的医生信息 */
    doctorId: {
      type: [String, Number],
      default: '',
    },
    hospitalId: {
      type: [String, Number],
      default: 0,
    },
  },
  emits: ['close', 'float-change', 'panel-width-change'],
  setup(props, { emit }) {
    // 构建 authInfo（响应式对象，conversationId 由 useAgentChat 内部管理）
    const authInfo = {
      get doctorId() { return String(props.doctorId || ''); },
      get hospitalId() { return Number(props.hospitalId) || 0; },
      conversationId: '',
    };

    const {
      status: wsStatus,
      messages,
      isTyping,
      isGenerating,
      errorBanner,
      init,
      sendMessage,
      stopGeneration,
      retryMessage,
      disconnect,
      newConversation,
      sendApprovalDecision,
      sendCardAction,
    } = useAgentChat(authInfo);

    const statusText = computed(() => {
      const map = {
        connecting: '连接中',
        connected: '已连接',
        disconnected: '已断开',
        error: '连接失败',
      };
      return map[wsStatus.value] || '未知';
    });

    // ---- 彩蛋：测试环境双击 logo 打开 debug 调试台 ----
    const logoClickCount = ref(0);
    let logoClickTimer = null;

    function isTestEnv() {
      const host = window.location.hostname;
      return host === 'localhost' || host === '127.0.0.1' || host.includes('test-');
    }

    function handleLogoClick() {
      if (!isTestEnv()) return;
      logoClickCount.value += 1;
      if (logoClickTimer) clearTimeout(logoClickTimer);
      if (logoClickCount.value >= 2) {
        logoClickCount.value = 0;
        const params = new URLSearchParams();
        if (props.doctorId) params.set('doctorId', String(props.doctorId));
        if (props.hospitalId) params.set('hospitalId', String(props.hospitalId));
        const debugUrl = `/#/debug/tools?${params.toString()}`;
        window.open(debugUrl, '_blank');
        return;
      }
      logoClickTimer = setTimeout(() => {
        logoClickCount.value = 0;
      }, 500);
    }

    // ---- 悬浮模式 ----
    const isFloat = ref(false);
    const floatPos = ref({ right: 20, top: 80 });
    const panelRef = ref(null);

    // 嵌入式宽度
    const embedWidth = ref(EMBED_DEFAULT_WIDTH);
    // 悬浮模式尺寸
    const floatSize = ref({ width: FLOAT_DEFAULT_WIDTH, height: FLOAT_DEFAULT_HEIGHT });

    // 从 localStorage 恢复
    try {
      const savedMode = localStorage.getItem(FLOAT_MODE_KEY);
      if (savedMode === '1') isFloat.value = true;
      const savedPos = localStorage.getItem(FLOAT_POS_KEY);
      if (savedPos) floatPos.value = JSON.parse(savedPos);
      const savedEmbedWidth = localStorage.getItem(EMBED_WIDTH_KEY);
      if (savedEmbedWidth) {
        const w = parseInt(savedEmbedWidth, 10);
        if (w >= EMBED_MIN_WIDTH && w <= EMBED_MAX_WIDTH) embedWidth.value = w;
      }
      const savedFloatSize = localStorage.getItem(FLOAT_SIZE_KEY);
      if (savedFloatSize) {
        const s = JSON.parse(savedFloatSize);
        if (s.width >= FLOAT_MIN_WIDTH && s.width <= FLOAT_MAX_WIDTH) floatSize.value.width = s.width;
        const maxH = window.innerHeight * 0.9;
        if (s.height >= FLOAT_MIN_HEIGHT && s.height <= maxH) floatSize.value.height = s.height;
      }
    } catch { /* ignore */ }

    // 面板整体样式（嵌入式 / 悬浮模式）
    const panelStyle = computed(() => {
      if (isFloat.value) {
        return {
          right: `${floatPos.value.right}px`,
          top: `${floatPos.value.top}px`,
          bottom: 'auto',
          transform: 'none',
          width: `${floatSize.value.width}px`,
          height: `${floatSize.value.height}px`,
        };
      }
      return {
        width: `${embedWidth.value}px`,
      };
    });

    function toggleFloatMode() {
      isFloat.value = !isFloat.value;
      try {
        localStorage.setItem(FLOAT_MODE_KEY, isFloat.value ? '1' : '0');
      } catch { /* ignore */ }
      emit('float-change', isFloat.value);
      // 切换模式时通知父组件当前嵌入宽度
      if (!isFloat.value) {
        emit('panel-width-change', embedWidth.value);
      }
    }

    // ---- 嵌入式左边缘 resize ----
    let embedResizeStartX = 0;
    let embedResizeStartWidth = 0;
    let isEmbedResizing = false;

    function startEmbedResize(e) {
      isEmbedResizing = true;
      embedResizeStartX = e.clientX;
      embedResizeStartWidth = embedWidth.value;
      document.addEventListener('mousemove', onEmbedResizeMove);
      document.addEventListener('mouseup', onEmbedResizeEnd);
      document.body.classList.add('no-select');
    }

    function onEmbedResizeMove(e) {
      if (!isEmbedResizing) return;
      // 向左拖拽 → 宽度增大；向右拖拽 → 宽度减小
      const dx = embedResizeStartX - e.clientX;
      const newWidth = Math.min(EMBED_MAX_WIDTH, Math.max(EMBED_MIN_WIDTH, embedResizeStartWidth + dx));
      embedWidth.value = newWidth;
      emit('panel-width-change', newWidth);
    }

    function onEmbedResizeEnd() {
      isEmbedResizing = false;
      document.removeEventListener('mousemove', onEmbedResizeMove);
      document.removeEventListener('mouseup', onEmbedResizeEnd);
      document.body.classList.remove('no-select');
      try {
        localStorage.setItem(EMBED_WIDTH_KEY, String(embedWidth.value));
      } catch { /* ignore */ }
    }

    // ---- 悬浮模式拖拽移动 ----
    let dragStartX = 0;
    let dragStartY = 0;
    let dragStartRight = 0;
    let dragStartTop = 0;
    let isDragging = false;

    function startDrag(e) {
      if (e.target.closest('button') || e.target.closest('.doctor-agent-panel__header-actions')) return;
      isDragging = true;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      dragStartRight = floatPos.value.right;
      dragStartTop = floatPos.value.top;
      document.addEventListener('mousemove', onDragMove);
      document.addEventListener('mouseup', onDragEnd);
      e.preventDefault();
    }

    function onDragMove(e) {
      if (!isDragging) return;
      const dx = e.clientX - dragStartX;
      const dy = e.clientY - dragStartY;
      const newRight = Math.max(0, dragStartRight - dx);
      const newTop = Math.max(0, dragStartTop + dy);
      floatPos.value = { right: newRight, top: newTop };
    }

    function onDragEnd() {
      isDragging = false;
      document.removeEventListener('mousemove', onDragMove);
      document.removeEventListener('mouseup', onDragEnd);
      try {
        localStorage.setItem(FLOAT_POS_KEY, JSON.stringify(floatPos.value));
      } catch { /* ignore */ }
    }

    // ---- 悬浮模式四边/四角 resize ----
    let floatResizeDir = '';
    let floatResizeStartX = 0;
    let floatResizeStartY = 0;
    let floatResizeStartWidth = 0;
    let floatResizeStartHeight = 0;
    let floatResizeStartRight = 0;
    let floatResizeStartTop = 0;
    let isFloatResizing = false;

    function startFloatResize(dir, e) {
      floatResizeDir = dir;
      isFloatResizing = true;
      floatResizeStartX = e.clientX;
      floatResizeStartY = e.clientY;
      floatResizeStartWidth = floatSize.value.width;
      floatResizeStartHeight = floatSize.value.height;
      floatResizeStartRight = floatPos.value.right;
      floatResizeStartTop = floatPos.value.top;
      document.addEventListener('mousemove', onFloatResizeMove);
      document.addEventListener('mouseup', onFloatResizeEnd);
      document.body.classList.add('no-select');
    }

    function onFloatResizeMove(e) {
      if (!isFloatResizing) return;
      const dx = e.clientX - floatResizeStartX;
      const dy = e.clientY - floatResizeStartY;
      const maxH = window.innerHeight * 0.9;
      let newWidth = floatResizeStartWidth;
      let newHeight = floatResizeStartHeight;
      let newRight = floatResizeStartRight;
      let newTop = floatResizeStartTop;

      const dir = floatResizeDir;

      // 水平方向
      if (dir.includes('e')) {
        // 右边缘向右拉 → 宽度增大，right 减小
        newWidth = Math.min(FLOAT_MAX_WIDTH, Math.max(FLOAT_MIN_WIDTH, floatResizeStartWidth + dx));
        newRight = floatResizeStartRight - (newWidth - floatResizeStartWidth);
      }
      if (dir.includes('w')) {
        // 左边缘向左拉 → 宽度增大，right 增大
        newWidth = Math.min(FLOAT_MAX_WIDTH, Math.max(FLOAT_MIN_WIDTH, floatResizeStartWidth - dx));
        newRight = floatResizeStartRight + (newWidth - floatResizeStartWidth);
      }

      // 垂直方向
      if (dir.includes('s')) {
        // 下边缘向下拉 → 高度增大
        newHeight = Math.min(maxH, Math.max(FLOAT_MIN_HEIGHT, floatResizeStartHeight + dy));
      }
      if (dir.includes('n')) {
        // 上边缘向上拉 → 高度增大，top 减小
        newHeight = Math.min(maxH, Math.max(FLOAT_MIN_HEIGHT, floatResizeStartHeight - dy));
        newTop = floatResizeStartTop + (newHeight - floatResizeStartHeight) * -1;
        // 防止超出顶部
        if (newTop < 0) {
          newHeight = floatResizeStartHeight + floatResizeStartTop;
          newTop = 0;
        }
      }

      floatSize.value = { width: newWidth, height: newHeight };
      floatPos.value = { right: Math.max(0, newRight), top: Math.max(0, newTop) };
    }

    function onFloatResizeEnd() {
      isFloatResizing = false;
      document.removeEventListener('mousemove', onFloatResizeMove);
      document.removeEventListener('mouseup', onFloatResizeEnd);
      document.body.classList.remove('no-select');
      try {
        localStorage.setItem(FLOAT_SIZE_KEY, JSON.stringify(floatSize.value));
        localStorage.setItem(FLOAT_POS_KEY, JSON.stringify(floatPos.value));
      } catch { /* ignore */ }
    }

    function handleSend(text, attachments) { sendMessage(text, attachments); }
    function handleStop() { stopGeneration(); }
    function handleRetryMessage(messageId) { retryMessage(messageId); }
    function handleNewConversation() { newConversation(); }
    function handleApprovalDecision(approvalId, decision) { sendApprovalDecision(approvalId, decision); }
    function handleCardAction(cardId, action, value) { sendCardAction(cardId, action, value); }
    function handleRetryConnect() { disconnect(); init(); }

    // 防止 init() 被重复调用（onMounted 和 watch 可能同时满足条件）
    let initialized = false;
    function safeInit() {
      if (initialized) return;
      initialized = true;
      init();
    }

    onMounted(() => {
      // doctorId 已有值则直接 init；否则等 watch 触发
      if (props.doctorId) {
        safeInit();
      }
      emit('float-change', isFloat.value);
      emit('panel-width-change', embedWidth.value);
    });

    // doctorId 从空变为有值时（父组件异步加载完 doctorInfo），自动触发 init
    watch(
      () => props.doctorId,
      (newVal) => {
        if (newVal) safeInit();
      },
    );

    onUnmounted(() => {
      disconnect();
      document.removeEventListener('mousemove', onDragMove);
      document.removeEventListener('mouseup', onDragEnd);
      document.removeEventListener('mousemove', onEmbedResizeMove);
      document.removeEventListener('mouseup', onEmbedResizeEnd);
      document.removeEventListener('mousemove', onFloatResizeMove);
      document.removeEventListener('mouseup', onFloatResizeEnd);
    });

    return {
      wsStatus,
      messages,
      isTyping,
      isGenerating,
      errorBanner,
      statusText,
      isFloat,
      panelStyle,
      floatPos,
      floatSize,
      embedWidth,
      panelRef,
      handleLogoClick,
      toggleFloatMode,
      startDrag,
      startEmbedResize,
      startFloatResize,
      handleSend,
      handleStop,
      handleRetryMessage,
      handleNewConversation,
      handleRetryConnect,
      handleApprovalDecision,
      handleCardAction,
    };
  },
};
</script>

<style scoped>
/* ---- 侧边栏模式（默认） ---- */
.doctor-agent-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  height: 100vh;
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  z-index: 500;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 1px solid #e8ecf0;
}
.doctor-agent-panel--visible {
  transform: translateX(0);
}

/* ---- 悬浮模式 ---- */
.doctor-agent-panel--float {
  position: fixed;
  width: 400px;
  height: 600px;
  border-radius: 16px;
  border: 1px solid #e8ecf0;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
  transform: none !important;
  transition: box-shadow 0.2s ease, opacity 0.2s ease;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
.doctor-agent-panel--float.doctor-agent-panel--visible {
  transform: none !important;
  opacity: 1;
  pointer-events: auto;
}

/* ---- resize 手柄 ---- */
/* 嵌入式：左边缘 */
.doctor-agent-panel__resize-handle--left {
  position: absolute;
  left: -4px;
  top: 0;
  width: 8px;
  height: 100%;
  cursor: ew-resize;
  z-index: 10;
  background: transparent;
}
.doctor-agent-panel__resize-handle--left:hover,
.doctor-agent-panel__resize-handle--left:active {
  background: rgba(0, 82, 217, 0.15);
  border-radius: 4px;
}

/* 悬浮模式：四边 */
.doctor-agent-panel__resize-handle--n {
  position: absolute;
  top: -4px;
  left: 8px;
  right: 8px;
  height: 8px;
  cursor: ns-resize;
  z-index: 10;
}
.doctor-agent-panel__resize-handle--s {
  position: absolute;
  bottom: -4px;
  left: 8px;
  right: 8px;
  height: 8px;
  cursor: ns-resize;
  z-index: 10;
}
.doctor-agent-panel__resize-handle--e {
  position: absolute;
  right: -4px;
  top: 8px;
  bottom: 8px;
  width: 8px;
  cursor: ew-resize;
  z-index: 10;
}
.doctor-agent-panel__resize-handle--w {
  position: absolute;
  left: -4px;
  top: 8px;
  bottom: 8px;
  width: 8px;
  cursor: ew-resize;
  z-index: 10;
}
/* 悬浮模式：四角 */
.doctor-agent-panel__resize-handle--ne {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  cursor: nesw-resize;
  z-index: 11;
}
.doctor-agent-panel__resize-handle--nw {
  position: absolute;
  top: -4px;
  left: -4px;
  width: 12px;
  height: 12px;
  cursor: nwse-resize;
  z-index: 11;
}
.doctor-agent-panel__resize-handle--se {
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  cursor: nwse-resize;
  z-index: 11;
}
.doctor-agent-panel__resize-handle--sw {
  position: absolute;
  bottom: -4px;
  left: -4px;
  width: 12px;
  height: 12px;
  cursor: nesw-resize;
  z-index: 11;
}

/* ---- 头部 ---- */
.doctor-agent-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 72px;
  background: #fff;
  border-bottom: 1px solid #eef0f3;
  flex-shrink: 0;
}
.doctor-agent-panel__header--draggable {
  cursor: move;
  user-select: none;
}
.doctor-agent-panel__header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.doctor-agent-panel__logo {
  flex-shrink: 0;
}
.doctor-agent-panel__logo-img {
  width: 52px;
  height: 52px;
  object-fit: contain;
}
.doctor-agent-panel__title-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.doctor-agent-panel__title {
  font-family: TencentSans, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 32px;
  background: linear-gradient(90deg, #6b59f1 14.53%, #4bc5eb 101.49%);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}
.doctor-agent-panel__status {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #86909c;
}
.doctor-agent-panel__status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #86909c;
  flex-shrink: 0;
}
.doctor-agent-panel__status--connected .doctor-agent-panel__status-dot {
  background: #07c160;
  box-shadow: 0 0 5px rgba(7, 193, 96, 0.7);
}
.doctor-agent-panel__status--connected {
  color: #07c160;
}
.doctor-agent-panel__status--connecting .doctor-agent-panel__status-dot {
  background: #ff9500;
  animation: agent-status-blink 1s ease-in-out infinite;
}
.doctor-agent-panel__status--connecting {
  color: #ff9500;
}
.doctor-agent-panel__status--error .doctor-agent-panel__status-dot {
  background: #e34d59;
}
.doctor-agent-panel__status--error {
  color: #e34d59;
}
@keyframes agent-status-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.doctor-agent-panel__header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.doctor-agent-panel__action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #86909c;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  font-size: 18px;
  font-weight: 300;
}
.doctor-agent-panel__action-btn:hover {
  background: #f0f2f5;
  color: #1a202c;
}
.doctor-agent-panel__action-btn--close:hover {
  background: #fff2f0;
  color: #e34d59;
}

/* ---- 底部免责声明 ---- */
.doctor-agent-panel__disclaimer {
  padding: 6px 14px 10px;
  text-align: center;
  font-size: 11px;
  color: #b0b8c4;
  background: #fff;
  border-top: 1px solid #eef0f3;
  flex-shrink: 0;
}

/* ---- 错误横幅 ---- */
.doctor-agent-panel__error-banner {
  padding: 8px 16px;
  background: #e34d59;
  color: #fff;
  font-size: 13px;
  line-height: 1.5;
  flex-shrink: 0;
}
.agent-banner-enter-active, .agent-banner-leave-active {
  transition: max-height 0.3s ease, opacity 0.2s ease;
  overflow: hidden;
}
.agent-banner-enter, .agent-banner-leave-to {
  max-height: 0;
  opacity: 0;
}
.agent-banner-enter-to, .agent-banner-leave {
  max-height: 60px;
  opacity: 1;
}

/* ---- 连接中状态 ---- */
.doctor-agent-panel__connecting {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #86909c;
  font-size: 14px;
}
.doctor-agent-panel__connecting-spinner {
  width: 28px;
  height: 28px;
  border: 2.5px solid #e2e8f0;
  border-top-color: #0052D9;
  border-radius: 50%;
  animation: agent-spin 0.8s linear infinite;
}
@keyframes agent-spin {
  to { transform: rotate(360deg); }
}

/* ---- 错误状态 ---- */
.doctor-agent-panel__error-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #86909c;
  font-size: 14px;
}
.doctor-agent-panel__retry-btn {
  padding: 8px 20px;
  border-radius: 20px;
  border: 1.5px solid #0052D9;
  background: transparent;
  color: #0052D9;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s;
}
.doctor-agent-panel__retry-btn:hover {
  background: #e8f0fe;
}
</style>
